import { resolveId } from '@medplum/core'
import type { DocumentReference, Reference } from '@medplum/fhirtypes'
import {
  getAttachmentConversionState,
  isAttachmentConversionPending,
} from '@seen/core/attachmentConversion'
import { referenceDisplay } from '@seen/core/display'
import { IDT } from '@seen/core/models/care-teams/idt'
import {
  DOCUMENT_CATEGORY_OPTIONS,
  DOCUMENT_TYPE_OPTIONS_BY_CATEGORY,
  type DocumentCategory,
  type DocumentTypes,
} from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Order } from '@seen/core/models/orders/Order'
import type { Participant } from '@seen/core/models/patients/participant'
import { Signature } from '@seen/core/models/signature'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { Role } from '@seen/core/roles'
import { queryTypesToString } from '@seen/core/search'
import { Button, cn, Separator, TypographyOverline } from '@seen/ui'
import {
  IconChevronRight,
  IconFile,
  IconPencil,
  IconSparkles,
} from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { compact } from 'lodash-es'
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { Activity } from '#src/components/activity/Activity.tsx'
import { AttachmentDisplay } from '#src/components/attachments/AttachmentDisplay.tsx'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import {
  Combobox,
  type SuggestedOption,
} from '#src/components/core/combobox/Combobox.tsx'
import {
  ComboboxMulti,
  type SuggestedMultiOption,
} from '#src/components/core/combobox/ComboboxMulti.tsx'
import { MultiSelect } from '#src/components/core/combobox/MultiSelect.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DocumentReferenceActivity } from '#src/components/document-reference/DocumentReferenceActivity.tsx'
import { OtherLabCopies } from '#src/components/document-reference/OtherLabCopies.tsx'
import { useFolders } from '#src/components/document-reference/useFolders.ts'
import { SendFaxModal } from '#src/components/fax/SendFaxModal.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { OrderOption } from '#src/components/OrderOption.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { createTaskFilter } from '#src/hooks/inbox/filter.ts'
import { useMarkRelatedInboxItemsAsDone } from '#src/hooks/inbox/useMarkRelatedInboxItemsAsDone.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'

function SuggestedOptionTooltip({
  confidence,
  reason,
}: {
  confidence?: number
  reason?: string
}): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
        <span className="inline-flex">
          <IconSparkles className="size-4 text-green-600" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <div className="flex flex-col gap-1">
          {confidence !== undefined && (
            <span className="font-medium">
              {Math.round(confidence * 100)}% confidence
            </span>
          )}
          {reason && <span>{reason}</span>}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function getSuggestedOrderOptions(
  predictedOrders: ExtendedDocumentReference['predictedOrders'],
  orders: Order[] | undefined
): SuggestedMultiOption<string>[] {
  if (!predictedOrders?.length) {
    return []
  }

  const suggestions: SuggestedMultiOption<string>[] = []

  for (const p of predictedOrders) {
    const orderId = resolveId(p.reference)
    if (!orderId) {
      continue
    }

    const order = orders?.find((o) => o.id === orderId)
    if (!order) {
      continue
    }

    suggestions.push({
      label: <OrderOption order={p.reference} />,
      value: orderId,
      keywords: order.searchKeywords ?? [],
      tooltip: (
        <SuggestedOptionTooltip confidence={p.confidence} reason={p.reason} />
      ),
    })
  }

  return suggestions
}

// The predicted participant is only offered when it resolves to a participant
// already in the loaded roster, so the suggestion can never name someone the
// dropdown itself cannot select.
export function getSuggestedParticipantOptions(
  predictedSubject: ExtendedDocumentReference['predictedSubject'],
  participants: Participant[]
): SuggestedOption<string>[] {
  if (!predictedSubject) {
    return []
  }

  const participantId = resolveId(predictedSubject.reference)
  if (!participantId) {
    return []
  }

  const participant = participants.find((p) => p.id === participantId)
  if (!participant) {
    return []
  }

  return [
    {
      label: <ParticipantOption participant={predictedSubject.reference} />,
      value: participantId,
      keywords: participant.searchKeywords,
      tooltip: (
        <SuggestedOptionTooltip
          confidence={predictedSubject.confidence}
          reason={predictedSubject.reason}
        />
      ),
    },
  ]
}

interface DocumentReferenceDisplayProps {
  documentReference: Reference<DocumentReference>
  onDelete?: () => void
  hideDelete?: boolean
  hideParticipant?: boolean
  onReviewComplete?: (documentReference: Reference<DocumentReference>) => void
  /**
   * When true (default), the component polls every 3s while at least one
   * attachment is in `attachment-conversion: pending` state so the modal can
   * auto-refresh once HEIC conversion completes. Disable this in contexts
   * where many `DocumentReferenceDisplay` instances may mount simultaneously
   * (e.g. lists of document-review tasks/cards) to avoid a poll storm — those
   * call sites should rely on a manual refresh instead.
   */
  pollWhilePending?: boolean
}

// Why: covers the 20-min server-side conversion watchdog grace window plus a
// 5-min buffer; beyond this we assume the conversion is genuinely stuck and
// stop polling so a long-lived modal doesn't burn requests forever.
const MAX_POLL_DURATION_MS = 25 * 60 * 1000
const POLL_INTERVAL_MS = 3000

/**
 * Decides the next polling interval for `DocumentReferenceDisplay` while
 * waiting on attachment conversion. Returns `false` to stop polling, or a
 * millisecond delay otherwise. Extracted as a pure function so unit tests can
 * exercise the activate/stop transitions without rendering the component.
 *
 * @param docRef - The document reference to evaluate (or `undefined` while loading).
 * @param now - Current epoch time in ms; injectable for tests.
 * @returns A polling interval in ms, or `false` to stop polling.
 */
export function getDocumentConversionPollInterval(
  docRef: ExtendedDocumentReference | undefined,
  now: number = Date.now()
): number | false {
  if (!docRef) {
    return false
  }
  const pendingAttachments = docRef.attachments.filter((a) =>
    isAttachmentConversionPending(a)
  )
  if (pendingAttachments.length === 0) {
    return false
  }
  // Why: stop polling only when EVERY pending attachment is past the watchdog
  // window. If even one is still recent, the server may still complete it.
  // Fail-safe: a pending attachment with no `startedAt` is treated as expired
  // so a missing timestamp can never keep us polling forever.
  const allPastWatchdog = pendingAttachments.every((a) => {
    const startedAt = getAttachmentConversionState(a)?.startedAt
    if (!startedAt) {
      return true
    }
    return now - new Date(startedAt).getTime() > MAX_POLL_DURATION_MS
  })
  if (allPastWatchdog) {
    return false
  }
  return POLL_INTERVAL_MS
}

export function DocumentReferenceDisplay({
  documentReference,
  onDelete,
  hideDelete = false,
  hideParticipant = false,
  onReviewComplete,
  pollWhilePending = true,
}: DocumentReferenceDisplayProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const folders = useFolders()
  const queryClient = useQueryClient()
  const { data: docRef, isLoading } = useSearchOne(
    ExtendedDocumentReference,
    {
      _id: resolveId(documentReference),
      ...ExtendedDocumentReference.defaultSearchQuery(),
    },
    {
      query: {
        refetchInterval: pollWhilePending
          ? (query) =>
              getDocumentConversionPollInterval(query.state.data ?? undefined)
          : false,
      },
    }
  )

  const hasPendingConversion =
    docRef?.attachments.some((a) => isAttachmentConversionPending(a)) ?? false
  const previousHasPendingConversion = useRef(hasPendingConversion)
  useEffect(() => {
    // Why: read and list queries are independent cache keys. When the modal
    // observes the pending → not-pending edge, invalidate the list query so
    // DocumentReferencesTable cells (which render their own
    // attachment-conversion state) reflect the fresh data without a manual
    // refresh.
    if (previousHasPendingConversion.current && !hasPendingConversion) {
      void queryClient.invalidateQueries({
        queryKey: ['DocumentReference', 'searchResourcePages'],
      })
    }
    previousHasPendingConversion.current = hasPendingConversion
  }, [hasPendingConversion, queryClient])
  const patch = usePatchMutation<ExtendedDocumentReference>()
  const transactionMutation = useTransactionMutation()
  const { mutate: mutateTransaction } = transactionMutation

  const open = useDeleteConfirmationStore((state) => state.open)
  const currentPractitioner = useCurrentPractitioner()
  const { roles } = useCurrentPractitionerPerms()
  const isPCP = roles.includes(Role.PCP)

  const participants = useParticipants()

  const patient = docRef?.subject
  const { data: orders } = useSearch(
    Order,
    Order.byNonDraftOrCancelled(patient),
    { query: { enabled: !!patient } }
  )
  const linkedOrders = useSearch(
    Order,
    {
      _id: docRef?.orders?.map(resolveId) ?? [],
    },
    { query: { enabled: !!docRef?.orders?.length } }
  )

  const { data: signatures = [] } = useSearch(
    Signature,
    docRef ? Signature.queryByTarget(docRef.reference.reference) : undefined,
    { query: { enabled: !!docRef } }
  )

  const { data: reviewTask, isLoading: isReviewTaskLoading } = useSearchOne(
    DocumentReviewTask,
    docRef ? DocumentReviewTask.byDocumentActive(docRef.reference) : undefined
  )

  const { data: idt, isLoading: isIdtLoading } = useSearchOne(
    IDT,
    !isReadOnly && !isReviewTaskLoading && !reviewTask && docRef?.subject
      ? IDT.byParticipant(docRef.subject)
      : undefined
  )
  const pcpRef = idt?.getMemberByRole(Role.PCP)?.practitioner

  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  const setTaskComposerDefaultValues = useTaskComposerStore(
    (s) => s.setDefaultValues
  )
  const setTaskComposerOpen = useTaskComposerStore((s) => s.setOpen)

  const hasCurrentPractitionerSigned = useMemo(() => {
    if (!docRef || !isPCP) {
      return false
    }
    return ExtendedDocumentReference.hasPractitionerSigned(
      signatures,
      currentPractitioner.reference
    )
  }, [docRef, isPCP, signatures, currentPractitioner.reference])

  const signButtonText = hasCurrentPractitionerSigned
    ? 'Signed'
    : 'Sign Document'

  const handleSign = useCallback(() => {
    if (!docRef) {
      return
    }

    const signature = new Signature({
      target: docRef.reference,
      who: currentPractitioner.reference,
    })

    mutateTransaction(
      (transaction) => {
        transaction.create(signature, {
          request: {
            ifNoneExist: queryTypesToString(
              Signature.queryByTargetAndWho(
                docRef.reference,
                currentPractitioner.reference
              )
            ),
          },
        })
        transaction.patch(docRef, (doc) => {
          doc.markAsConfirmed()
        })
        if (reviewTask) {
          transaction.patch(reviewTask, (t) => {
            t.state = 'done'
            if (docRef.subject) {
              t.participant = docRef.subject
            }
          })
        }
      },
      {
        onSuccess: () => {
          toast.success('Document signed successfully')
          if (reviewTask) {
            markRelatedInboxItemsAsDone(
              createTaskFilter([
                {
                  taskReferenceString: reviewTask.reference.reference,
                  state: 'done',
                },
              ])
            )
          }
          onReviewComplete?.(documentReference)
        },
        onError: (error) => {
          console.error('Failed to sign document', error)
          toast.error('Failed to sign document')
        },
      }
    )
  }, [
    docRef,
    currentPractitioner.reference,
    mutateTransaction,
    reviewTask,
    markRelatedInboxItemsAsDone,
    onReviewComplete,
    documentReference,
  ])

  const documentTypeOptions = useMemo(() => {
    if (!docRef) {
      return undefined
    }
    const allOptions = docRef.categories.flatMap(
      (category) => DOCUMENT_TYPE_OPTIONS_BY_CATEGORY[category] ?? []
    )
    return allOptions.length > 0 ? allOptions : undefined
  }, [docRef])

  const suggestedOrderOptions = useMemo(
    () => getSuggestedOrderOptions(docRef?.predictedOrders, orders),
    [docRef?.predictedOrders, orders]
  )

  const suggestedParticipantOptions = useMemo(
    () =>
      getSuggestedParticipantOptions(docRef?.predictedSubject, participants),
    [docRef?.predictedSubject, participants]
  )

  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [showSendFaxModal, setShowSendFaxModal] = useState(false)

  if (!isLoading && !docRef) {
    return <p>This document is not available in clinical documents.</p>
  }

  if (isLoading || !docRef) {
    return <Loader />
  }

  const author = referenceDisplay(docRef.author)

  return (
    <div className="space-y-4 flex h-full flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconFile size={40} strokeWidth={1} />
          <div className="flex flex-col">
            {/* The title resolves to `description` when one is set, so editing
                the title writes `description`; it falls back to the filename. */}
            {!isReadOnly && isEditingTitle ? (
              <ResourceFieldForm
                instance={docRef}
                valueFn={(docRef) => docRef.description}
                patchFn={(docRef, value) => {
                  docRef.description = value
                }}
                debounceTyping
                render={({ field: { value, onBlur, ...fieldRest } }) => (
                  <RichTextEditor
                    defaultValue={value}
                    placeholder="Title"
                    variant="unstyled"
                    className="font-bold"
                    autofocus="end"
                    onBlur={() => {
                      onBlur?.()
                      setIsEditingTitle(false)
                    }}
                    {...fieldRest}
                  />
                )}
              />
            ) : (
              <span className="group flex items-center gap-1">
                <RichText value={docRef.title} inline className="font-bold" />
                {!isReadOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-4 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                    aria-label="Edit title"
                    onClick={() => setIsEditingTitle(true)}
                  >
                    <IconPencil className="size-3" />
                  </Button>
                )}
              </span>
            )}
            {/* Service date (the clinically meaningful date) shown in the
                header. Falls back to a dash when none is recorded; an edit
                pencil is revealed on hover to set/change/clear it. */}
            <span className="group flex items-center gap-1 text-sm text-muted-foreground">
              {docRef.serviceDate ? (
                <HoverDate date={docRef.serviceDate} format="date" />
              ) : (
                <span>—</span>
              )}
              {!isReadOnly && (
                <DatePicker
                  asChild
                  value={docRef.serviceDate}
                  onChange={(value) => {
                    patch.mutate({
                      instance: docRef,
                      mutateFn(doc) {
                        doc.serviceDate = value
                      },
                    })
                  }}
                  allowUndefined
                  clearText="Clear"
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-4 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                    aria-label={
                      docRef.serviceDate
                        ? 'Edit service date'
                        : 'Add service date'
                    }
                  >
                    <IconPencil className="size-3" />
                  </Button>
                </DatePicker>
              )}
              {author && <span>· {author}</span>}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {docRef.attachments.map((attachment, i) => (
          <div
            key={attachment.id}
            className={
              i === docRef.attachments.length - 1 &&
              docRef.attachments.length % 2 === 1
                ? 'col-span-2'
                : ''
            }
          >
            <AttachmentDisplay
              value={attachment}
              documentReferenceId={docRef.id}
              onTitleChange={(title) => {
                patch.mutate({
                  instance: docRef,
                  mutateFn(docRef) {
                    docRef.setAttachmentTitleAtIndex(i, title)
                  },
                })
              }}
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-4">
        {!hideParticipant && (
          <div>
            <TypographyOverline>Participant</TypographyOverline>
            <ResourceFieldForm
              instance={docRef}
              valueFn={(docRef) => docRef.subject}
              patchFn={(docRef, value) => {
                docRef.subject = value ?? undefined
              }}
              render={({ field: { value, onChange, ...fieldRest } }) => (
                <Combobox
                  options={participants.map((p) => ({
                    label: <ParticipantOption participant={p.reference} />,
                    value: p.id,
                    keywords: p.searchKeywords,
                  }))}
                  suggestedOptions={suggestedParticipantOptions}
                  suggestedLabel="AI Suggested"
                  onSelect={(value) => {
                    onChange(
                      participants.find((p) => p.id === value)?.reference ??
                        null
                    )
                  }}
                  {...fieldRest}
                >
                  <Button variant="outline">
                    {value ? (
                      <ParticipantNameplate participant={value} />
                    ) : (
                      <UserPlaceholderNameplate>
                        Participant
                      </UserPlaceholderNameplate>
                    )}
                  </Button>
                </Combobox>
              )}
            />
          </div>
        )}
        <div>
          <TypographyOverline>Categories</TypographyOverline>
          <ResourceFieldForm
            instance={docRef}
            valueFn={(docRef) => docRef.categories}
            patchFn={(docRef, value) => {
              docRef.categories = value ?? []
            }}
            render={({ field: { value, onChange, ...fieldRest } }) => (
              <MultiSelect
                options={DOCUMENT_CATEGORY_OPTIONS}
                value={value}
                onChange={(values) => onChange(values as DocumentCategory[])}
                placeholder="Select categories…"
                variant="outline"
                {...fieldRest}
              />
            )}
          />
        </div>
        {folders.length > 0 && (
          <div>
            <TypographyOverline>Folder</TypographyOverline>
            <ResourceFieldForm
              instance={docRef}
              valueFn={(docRef) => docRef.folder}
              patchFn={(docRef, value) => {
                docRef.folder = value ?? undefined
              }}
              render={({ field: { value, onChange, ...fieldRest } }) => (
                <ComboboxButton
                  value={value?.id}
                  onChange={(value) =>
                    onChange(folders.find((f) => f.id === value) ?? null)
                  }
                  options={[
                    {
                      value: 'other',
                      label: 'Other',
                      keywords: ['Other'],
                    },
                    ...folders.map((folder) => ({
                      value: folder.id,
                      label: folder.name,
                      keywords: [folder.name],
                    })),
                  ]}
                  {...fieldRest}
                />
              )}
            />
          </div>
        )}
        <div>
          <TypographyOverline>Orders</TypographyOverline>
          <div className="flex items-center">
            <ResourceFieldForm
              instance={docRef}
              valueFn={(docRef) => docRef.orders}
              patchFn={(docRef, value) => {
                docRef.orders = value ?? undefined
              }}
              render={({ field: { value: _, onChange, ...fieldRest } }) => (
                <ComboboxMulti
                  options={
                    orders?.map((o) => ({
                      label: <OrderOption order={o.reference} />,
                      value: o.id,
                      keywords: o.searchKeywords,
                    })) ?? []
                  }
                  suggestedOptions={suggestedOrderOptions}
                  suggestedLabel="AI Suggested"
                  onSelect={(values) => {
                    const selectedOrders = orders?.filter((o) =>
                      values.includes(o.id)
                    )
                    onChange(selectedOrders?.map((o) => o.reference) ?? [])
                  }}
                  values={compact(docRef?.orders?.map(resolveId) ?? [])}
                  className="w-auto"
                >
                  <Button
                    variant="outline"
                    className={cn(docRef?.orders?.length && 'rounded-r-none')}
                    {...fieldRest}
                  >
                    {linkedOrders?.data?.length && docRef?.orders?.length
                      ? linkedOrders?.data
                          ?.map((o) => o.seenIdentifier)
                          .join(', ')
                      : 'Link Orders'}
                  </Button>
                </ComboboxMulti>
              )}
            />
            {(linkedOrders?.data?.length ?? 0) === 1 && (
              <Link
                to={`/Patient/${resolveId(linkedOrders?.data?.[0]?.participant)}/orders/${linkedOrders?.data?.[0]?.id}`}
              >
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-l-none border-l-0"
                >
                  <IconChevronRight className="size-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
        {!isReadOnly && docRef.subject && (
          <div>
            <TypographyOverline>PCP Review</TypographyOverline>
            <Button
              variant={hasCurrentPractitionerSigned ? 'outline' : 'default'}
              onClick={handleSign}
              disabled={
                !isPCP ||
                hasCurrentPractitionerSigned ||
                transactionMutation.isPending
              }
            >
              {signButtonText}
            </Button>
            {signatures
              .filter((sig) => !sig.voided)
              .map((sig) => (
                <div
                  key={sig.id}
                  className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"
                >
                  <span>
                    ✓ {referenceDisplay(sig.who) ?? sig.who.reference}
                  </span>
                  <span>·</span>
                  <HoverDate date={sig.recorded} format="date" />
                </div>
              ))}
          </div>
        )}
        {documentTypeOptions?.length && (
          <div>
            <TypographyOverline>Document Type</TypographyOverline>
            <ResourceFieldForm
              instance={docRef}
              valueFn={(docRef) => docRef.typeCode}
              patchFn={(docRef, value) => {
                docRef.typeCode = value ?? undefined
              }}
              render={({ field: { value, onChange, ...fieldRest } }) => (
                <ComboboxButton
                  value={value}
                  onChange={(value) => onChange(value as DocumentTypes)}
                  options={documentTypeOptions}
                  {...fieldRest}
                />
              )}
            />
          </div>
        )}
      </div>

      <OtherLabCopies documentReference={docRef} />

      <div className="mb-2">
        {docRef.aiSummary && (
          <div className="mt-2 text-2xs!">
            <TypographyOverline>AI Summary</TypographyOverline>
            <RichTextEditor
              defaultValue={docRef.aiSummary}
              variant="unstyled"
              editable={false}
            />
          </div>
        )}
      </div>

      {!isReadOnly && (
        <div className="mt-auto flex gap-2 pt-4">
          {!isReviewTaskLoading && !reviewTask && !isIdtLoading && (
            <Button
              variant="outline"
              onClick={() => {
                setTaskComposerDefaultValues({
                  code: DocumentReviewTask.code,
                  focus: docRef.reference,
                  participant: docRef.subject ?? undefined,
                  ownerPractitioner: pcpRef ?? undefined,
                  title: docRef.title ?? undefined,
                  description:
                    docRef.aiSummary ??
                    docRef.description ??
                    (docRef.title
                      ? `Review document: ${docRef.title}`
                      : undefined),
                  state: pcpRef ? 'in-progress' : undefined,
                })
                setTaskComposerOpen(true)
              }}
            >
              Assign for Review
            </Button>
          )}
          {!isReviewTaskLoading && reviewTask && (
            <Link
              to={
                reviewTask.participant
                  ? `/Patient/${resolveId(reviewTask.participant)}/tasks/${reviewTask.id}`
                  : `/tasks/${reviewTask.id}`
              }
            >
              <Button variant="outline">
                View Review Task
                {referenceDisplay(reviewTask.ownerPractitioner) &&
                  ` (${referenceDisplay(reviewTask.ownerPractitioner)})`}
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={() => setShowSendFaxModal(true)}>
            Send Fax
          </Button>
          {!hideDelete && (
            <Button
              variant="destructive"
              disabled={isReviewTaskLoading}
              onClick={() => {
                open({
                  title: 'Delete document',
                  description: 'Are you sure you want to delete this document?',
                  onConfirm: () => {
                    mutateTransaction(
                      (transaction) => {
                        transaction.patch(docRef, (doc) => {
                          doc.markDeleted()
                        })
                        if (reviewTask) {
                          transaction.patch(reviewTask, (t) => {
                            t.state = 'canceled'
                          })
                        }
                      },
                      {
                        onSuccess: () => {
                          onDelete?.()
                        },
                      }
                    )
                  },
                })
              }}
            >
              Delete
            </Button>
          )}
        </div>
      )}

      {docRef.dateString && (
        <p className="text-xs text-muted-foreground">
          Uploaded <HoverDate date={docRef.dateString} format="date" />
        </p>
      )}

      <Separator />

      <Activity resource={docRef} subject={docRef.subject} className="px-2">
        <DocumentReferenceActivity documentReference={docRef} />
      </Activity>

      <SendFaxModal
        open={showSendFaxModal}
        onOpenChange={setShowSendFaxModal}
        initialDocument={docRef}
      />
    </div>
  )
}
